import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import envConfig from '../../../env-config';
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import { FileDetails } from '../../../core/models/file-details.model';
import { FileStatus } from '../../../core/models/file-status.model';

interface FileStatusApiDto {
  id: number;
  status: string;
  displayOrder: number;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class FileDetailsRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = envConfig.bffUrl ?? '';

  getById(id: number): Observable<FileDetails | null> {
    this.logger.debug('FileDetailsRepository: Fetching file by ID', {
      id,
    });

    return this.http
      .get<FileDetails>(`${this.apiUrl}/api/v1/file/${id}`, {
        context: this.authContext(),
      })
      .pipe(
        map(response => response ?? null),
        tap(file =>
          this.logger.debug('FileDetailsRepository: File fetched', {
            id: file?.id,
            fileName: file?.fileName,
          })
        ),
        catchError(error => {
          this.logger.error(
            'FileDetailsRepository: Failed to fetch file',
            error
          );
          throw error;
        })
      );
  }

  getFileStatuses(): Observable<FileStatus[]> {
    this.logger.debug('FileDetailsRepository: Fetching file statuses');

    return this.http
      .get<FileStatusApiDto[]>(`${this.apiUrl}/api/v1/file-statuses`, {
        context: this.authContext(),
      })
      .pipe(
        map(response =>
          (response ?? [])
            .filter(dto => dto.isActive)
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(dto => this.mapStatusDtoToFileStatus(dto))
        ),
        tap(statuses =>
          this.logger.debug('FileDetailsRepository: File statuses fetched', {
            count: statuses.length,
          })
        ),
        catchError(error => {
          this.logger.error(
            'FileDetailsRepository: Failed to fetch file statuses',
            error
          );
          throw error;
        })
      );
  }

  updateStatus(id: number, fileStatusId: number): Observable<void> {
    this.logger.debug('FileDetailsRepository: Updating file status', {
      id,
      fileStatusId,
    });

    return this.http
      .put<void>(
        `${this.apiUrl}/api/v1/file/${id}/status`,
        { fileStatusId },
        {
          context: this.authContext(),
        }
      )
      .pipe(
        tap(() =>
          this.logger.debug('FileDetailsRepository: File status updated', {
            id,
            fileStatusId,
          })
        ),
        catchError(error => {
          this.logger.error(
            'FileDetailsRepository: Failed to update file status',
            error
          );
          throw error;
        })
      );
  }

  private mapStatusDtoToFileStatus(dto: FileStatusApiDto): FileStatus {
    return {
      id: dto.id,
      fileStatus: dto.status,
      active: dto.isActive,
      displayOrder: dto.displayOrder,
      createdDate: '',
      createdBy: '',
      modifiedDate: null,
      modifiedBy: null,
    };
  }
}
